<template>
  <div>
    <CDropdown
      :togglerText="$t('role.orders.rounds.wh.details.products.modifyBtn')"
      color="primary"
      :disabled="!canView(PERMS.ORDERS_EDIT)"
    >
      <CDropdownItem @click="setDelivered()">{{
        $t("role.orders.rounds.wh.details.tableActions.markAsReady")
      }}</CDropdownItem>
      <CDropdownItem @click="setNotDelivered()">{{
        $t("role.orders.rounds.wh.details.tableActions.markAsNotReady")
      }}</CDropdownItem>
      <CDropdownItem @click="createIncident(INCIDENT_TYPE.INCIDENT)">{{
        $t("role.orders.rounds.wh.details.tableActions.openIncident")
      }}</CDropdownItem>

      <CDropdownItem @click="createIncident(INCIDENT_TYPE.CANCELLATION)">{{
        $t("role.orders.rounds.wh.details.products.modify.cancel")
      }}</CDropdownItem>
    </CDropdown>

    <div class="d-flex justify-content-center m-3" v-if="loading">
      <CSpinner color="info" />
    </div>

    <ejs-grid
      ref="grid"
      v-show="!loading"
      :dataSource="rows"
      :allowSelection="true"
      :selectionSettings="selectionSettings"
      :detailTemplate="detailTemplate"
      :dataBound="onDataBound"
      @rowSelected="rowSelectedHandler($event)"
      @rowDeselected="rowDeselectedHandler($event)"
      @detailDataBound="onDetailDataBound"
    >
      <e-columns>
        <e-column type="checkbox" width="40" textAlign="left"></e-column>
        <e-column
          field="order.order.id"
          :headerText="$t('role.orders.rounds.orders.details.table.producer')"
          :template="nameTemplate"
        ></e-column>
        <e-column
          field="info"
          :headerText="$t('role.orders.manage.ddmp.detail.orders.table.info')"
          :template="infoTemplate"
          width="200"
        ></e-column>
        <e-column
          field="price"
          :headerText="$t('role.orders.manage.ddmp.detail.orders.table.price')"
          textAlign="right"
          :template="priceTemplate"
          width="150"
        ></e-column>
        <e-column
          field="action"
          headerText=""
          width="40"
          :template="actionsTemplate"
        ></e-column>
      </e-columns>
    </ejs-grid>
  </div>
</template>

<script>
import { DetailRow } from "@syncfusion/ej2-vue-grids";
import { Query, Predicate } from "@syncfusion/ej2-data";
import { GetDataManagerNew } from "../../../../../../ds";
import ProducersTableName from "./ProducersTableName";
import ProducersTableInfo from "./ProducersTableInfo";
import ProducersTablePrice from "./ProducersTablePrice";
import ProducersTableActions from "./ProducersTableActions";
import ProducersTableRowDetails from "./ProducersTableRowDetails";
import EventBus from "../../../../../../helpers/EventBus";
import { buildPredicate } from "../../../../../../helpers/common";
import orderManagementMixin from "../../../share/mixin";
import { filtersMap } from "../../filtersMap";
import { PERMS } from "../../../../../../helpers/acl";
import { INCIDENT_TYPE } from "../../../../../../config/global";

export default {
  name: "ProducersTable",

  mixins: [orderManagementMixin],

  props: {
    order: { type: Object, required: true },
  },

  provide: {
    grid: [DetailRow],
  },

  watch: {
    order() {
      this.itemSelected = Object.assign({});
      this.loadData();
    },
  },

  data: function () {
    const roleId = this.$store.state.role.id;
    const roundId = this.$store.state.rounds.round.id;
    const endpoint = filtersMap("wh.panel2.list.endpoint");
    const dm = GetDataManagerNew(endpoint, [roleId, roundId]);

    return {
      INCIDENT_TYPE,
      PERMS,
      dm: dm,
      loading: false,
      rows: [],
      selectionSettings: {
        persistSelection: false,
        enableToggle: true,
        checkboxOnly: true,
      },

      itemSelected: {},

      nameTemplate: () => {
        return { template: ProducersTableName };
      },

      infoTemplate: () => {
        return { template: ProducersTableInfo };
      },

      priceTemplate: () => {
        return { template: ProducersTablePrice };
      },

      actionsTemplate: () => {
        return { template: ProducersTableActions };
      },

      detailTemplate: () => {
        return {
          template: {
            extends: ProducersTableRowDetails,
            propsData: {
              grid: this.$refs.grid,
              order: this.order,
            },
          },
        };
      },
    };
  },

  mounted() {
    EventBus.$on("orders:detail", this.onDetailsSelected);
    EventBus.$on("orders:priceUpdate", this.onDetailPriceUpdate);
    this.loadData();
  },

  beforeDestroy() {
    EventBus.$off("orders:detail", this.onDetailsSelected);
    EventBus.$off("orders:priceUpdate", this.onDetailPriceUpdate);
  },

  methods: {
    loadData() {
      const query = this.prepareQuery();
      this.loading = true;

      this.dm
        .executeQuery(query)
        .then((response) => {
          this.loading = false;
          this.rows = response.result;
        })
        .catch((response) => {
          this.loading = false;
          console.error(response);
        });
    },

    prepareQuery() {
      const { keyword } = this.$store.state.rounds.filters;
      let predicate = new Predicate(
        "order.order.id",
        "equal",
        this.order.order.id
      );

      const map = filtersMap("wh.panel2.list.filters");
      predicate = buildPredicate(
        predicate,
        map,
        this.$store.state.rounds.filters
      );

      let query = new Query().where(predicate);

      const searchFields = filtersMap("wh.panel2.list.search", []);
      if (keyword && searchFields.length > 0) {
        query = query.search(keyword, searchFields);
      }

      return query;
    },

    getSelectedParentKey() {
      return "porders";
    },

    getSelectedChildrenKey() {
      return "details";
    },

    createIncident(type) {
      const selected = this.getSelected();
      if (selected) {
        const entities = [];
        Object.keys(selected).forEach((type) => {
          entities.push({ type, items: [...selected[type]] });
        });

        this.$store.commit("incidents/createResetAllEntities");
        this.$store.commit("incidents/createAddEntities", {
          type: type,
          entities,
          show: true,
        });
      }
    },

    setDelivered() {
      const selected = this.getSelected();
      if (selected) {
        this.$store
          .dispatch("rounds/updateStatusBatch", {
            status: filtersMap("wh.statusCode.next"),
            ...this.mapSelectedToIds(selected),
          })
          .then(this.onUpdateStatusSuccess);
      }
    },

    setNotDelivered() {
      const selected = this.getSelected();
      if (selected) {
        this.$store
          .dispatch("rounds/updateStatusBatch", {
            status: filtersMap("wh.statusCode.prev"),
            ...this.mapSelectedToIds(selected),
          })
          .then(this.onUpdateStatusSuccess);
      }
    },

    canView(perm) {
      return this.$store.getters["connections/havePermission"](perm);
    },
  },
};
</script>
